import React, { useState, useEffect, useRef } from "react";
import api, { fetchDatos } from "../services/api";
import useInternalLoader from "../hooks/useInternalLoader";
import html2canvas from "html2canvas";
import Select from "react-select";

import {
    agruparDataPorMes,
    agruparDataPorAnio,
    agruparDataPorDia,
} from "../utils/dataUtils";
import { calcularMeta } from "../utils/calcularMeta";

import BarraActivos from "../components/BarraActivos";
import PieCursos from "../components/PieCursos";
import RankingBarrasEstudiantes from "../components/RankingBarrasEstudiantes";
import RankingBarrasColegios from "../components/RankingBarrasColegios";
import ExportCSVButton from "../components/ExportCSVButton";
import { generatePDF } from "../components/ExportPDFButton";
import Loader from "../components/Loader";

import { FaChartBar, FaClock, FaUniversity, FaUserTie, FaCalendarAlt } from "react-icons/fa";
import "../styles/pages.css";

// Helper to get current week range
const obtenerRangoSemanaActual = () => {
    const hoy = new Date();
    const diaSemana = hoy.getDay();
    const diffLunes = diaSemana === 0 ? -6 : 1 - diaSemana;
    const diffDomingo = diaSemana === 0 ? 0 : 7 - diaSemana;

    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() + diffLunes);

    const domingo = new Date(hoy);
    domingo.setDate(hoy.getDate() + diffDomingo);

    const formatear = (fecha) => fecha.toISOString().split("T")[0];
    return { inicio: formatear(lunes), fin: formatear(domingo) };
};

const getUnifiedData = (dataActividad, pieCursosFinal, rankingEstudiantesFinal, filtros) => {
    const unified = [];

    // ACTIVIDAD
    unified.push({
        seccion: "Actividad",
        descripcion: `Actividad${filtros.unidadTiempo === "minutos" ? "Minutos" : "Horas"}_${filtros.yearSeleccionado ? filtros.yearSeleccionado.join("-") : "Todos"}`,
        fecha: "",
        total: "",
        curso: "",
        tiempo: "",
        profesor: "",
    });

    dataActividad.forEach((item) => {
        unified.push({
            seccion: "",
            descripcion: "",
            fecha: item.mes,
            total: item.total,
            curso: "",
            tiempo: "",
            profesor: "",
        });
    });

    unified.push({});

    // DISTRIBUCIÓN
    unified.push({
        seccion: "Distribución",
        descripcion: `DistribuciónTiempo_${filtros.yearSeleccionado ? filtros.yearSeleccionado.join("-") : "Todos"}`,
        fecha: "",
        total: "",
        curso: "",
        tiempo: "",
        profesor: "",
    });

    pieCursosFinal.forEach((item) => {
        unified.push({
            seccion: "",
            descripcion: "",
            fecha: item.name,
            total: item.value,
            curso: `${item.percentage}%`,
            tiempo: "",
            profesor: "",
        });
    });

    unified.push({});

    // RANKING
    unified.push({
        seccion: "Ranking",
        descripcion: `Ranking_${filtros.yearSeleccionado ? filtros.yearSeleccionado.join("-") : "Todos"}`,
        fecha: "",
        total: "",
        curso: "",
        tiempo: "",
        profesor: "",
    });

    rankingEstudiantesFinal.forEach((item) => {
        unified.push({
            seccion: "",
            descripcion: "",
            fecha: "",
            total: "",
            curso: item.rankingType,
            tiempo: item.minutos,
            profesor: item.name,
        });
    });

    return unified;
};

const GeneralPage = () => {
    const { internalLoading, runWithLoader } = useInternalLoader();

    const refBarra = useRef();
    const refPie = useRef();
    const refRanking = useRef();

    // --- UI State ---
    const [uiColegios, setUiColegios] = useState([]);
    const [uiYears, setUiYears] = useState([]);
    const [uiRol, setUiRol] = useState([{ value: "student", label: "Estudiante" }]);
    const [uiDocentes, setUiDocentes] = useState([]); // Nuevo estado para docentes específicos
    const [unidadTiempo, setUnidadTiempo] = useState("minutos");
    const [agrupacionModo, setAgrupacionModo] = useState("mes");
    const { inicio: fechaInicioDefault, fin: fechaFinDefault } = obtenerRangoSemanaActual();
    const [fechaInicio, setFechaInicio] = useState(fechaInicioDefault);
    const [fechaFin, setFechaFin] = useState(fechaFinDefault);

    // --- Applied Filters State ---
    const [appliedFilters, setAppliedFilters] = useState({
        roles: ["student"],
        years: [],
        docentes: [],
        unidad: "minutos",
        modo: "mes",
        inicio: fechaInicioDefault,
        fin: fechaFinDefault
    });

    // --- Data State ---
    const [generalData, setGeneralData] = useState([]);
    const [loadingGeneralData, setLoadingGeneralData] = useState(false);

    // Select Options
    const [opcionesColegios, setOpcionesColegios] = useState([]);
    const [opcionesAnios, setOpcionesAnios] = useState([]);
    const [opcionesDocentes, setOpcionesDocentes] = useState([]); // Opciones para el select de docentes

    // Charts Data
    const [dataActividad, setDataActividad] = useState([]);
    const [dataDistribucion, setDataDistribucion] = useState([]);
    const [pieCursosFinal, setPieCursosFinal] = useState([]);
    const [dataRanking, setDataRanking] = useState([]);
    
    // Data Reporte de Docentes
    const [reporteDocentesData, setReporteDocentesData] = useState([]);

    const [rankingTop, setRankingTop] = useState([]);
    const [rankingBottom, setRankingBottom] = useState([]);
    const [meta, setMeta] = useState(0);

    // 1. Fetch Colleges on mount
    useEffect(() => {
        api.get("/data/colegios")
            .then((res) => {
                const data = res.data;
                if (Array.isArray(data)) {
                    const options = data.map((col) => ({
                        value: col.id,
                        label: col.name,
                    }));
                    setOpcionesColegios(options);
                } else if (data && typeof data === 'object') {
                    const list = data.data || [];
                    if (Array.isArray(list)) {
                        const options = list.map((col) => ({
                            value: col.id,
                            label: col.name,
                        }));
                        setOpcionesColegios(options);
                    }
                }
            })
            .catch((err) => console.error("Error cargando colegios:", err));
    }, []);

    // 2. Handle Search
    const handleSearch = async () => {
        if (uiColegios.length === 0) {
            alert("Por favor seleccione al menos un colegio.");
            return;
        }

        setLoadingGeneralData(true);
        setGeneralData([]);

        try {
            const promises = uiColegios.map((col) => 
                fetchDatos(col.value).then(res => {
                    const dataArray = Array.isArray(res) ? res : (res.data || []);
                    // Inyectar el nombre del colegio para el reporte
                    return dataArray.map(d => ({ ...d, collegeName: col.label }));
                })
            );
            const results = await Promise.all(promises);
            const mergedData = results.flat();

            setGeneralData(mergedData);

            // Update years options based on new data
            const anios = Array.from(
                new Set(mergedData.map((d) => new Date(d.event_date).getFullYear().toString()))
            ).sort().map(year => ({ value: year, label: year }));
            setOpcionesAnios(anios);

            // Extraer lista única de profesores para el filtro
            const teacherRoles = ["editingteacher", "profesorgenerartarea", "teacher"];
            const docentesUnicos = Array.from(
                new Set(
                    mergedData
                        .filter(d => teacherRoles.includes(d.role_shortname))
                        .map(d => `${d.user_fname} ${d.user_sname}`)
                )
            ).sort().map(name => ({ value: name, label: name }));
            setOpcionesDocentes(docentesUnicos);

        } catch (error) {
            console.error("Error fetching general data:", error);
            if (error.response && error.response.status === 401) {
                alert("Su sesión ha expirado o no tiene permisos. Por favor inicie sesión nuevamente.");
            }
        } finally {
            setLoadingGeneralData(false);
        }
    };

    // Auto-apply local filters when UI state changes
    useEffect(() => {
        setAppliedFilters({
            roles: uiRol.map(r => r.value),
            years: uiYears.map(y => y.value),
            docentes: uiDocentes.map(d => d.value),
            unidad: unidadTiempo,
            modo: agrupacionModo,
            inicio: fechaInicio,
            fin: fechaFin
        });
    }, [uiRol, uiYears, uiDocentes, unidadTiempo, agrupacionModo, fechaInicio, fechaFin]);

    // 3. Process Data
    useEffect(() => {
        if (generalData && generalData.length > 0) {
            runWithLoader(() => {
                let filtrados = generalData;

                // Blacklist / Mantenimiento
                const EXCLUDED_IDS = [92037, 92178, 92037, 4, 98107, 92179, 92195];
                filtrados = filtrados.filter(d => {
                    if (EXCLUDED_IDS.includes(Number(d.userid))) return false;
                    const checkStr = (d.username || `${d.user_fname} ${d.user_sname}`).toLowerCase();
                    if (checkStr.includes("roboticminds") || checkStr.includes("@roboticminds.com.ec")) return false;
                    return true;
                });

                // Role Filter
                const rolesPermitidos = appliedFilters.roles;
                if (rolesPermitidos.length > 0) {
                    filtrados = filtrados.filter(d => {
                        if (d.role_shortname === "student") return rolesPermitidos.includes("student");
                        if (d.role_shortname === "editingteacher" || d.role_shortname === "profesorgenerartarea" || d.role_shortname === "teacher") {
                            return rolesPermitidos.includes("teacher");
                        }
                        return false;
                    });
                }

                // Date Filter (Día)
                if (appliedFilters.modo === "dia") {
                    const inicio = new Date(appliedFilters.inicio);
                    const fin = new Date(appliedFilters.fin);
                    filtrados = filtrados.filter((d) => {
                        const f = new Date(d.event_date);
                        return f >= inicio && f <= fin;
                    });
                }

                // Year Filter
                if (appliedFilters.years.length > 0 && appliedFilters.modo !== "dia") {
                    filtrados = filtrados.filter(d => {
                        const y = new Date(d.event_date).getFullYear().toString();
                        return appliedFilters.years.includes(y);
                    });
                }

                // ---- REPORTE EXCLUSIVO PARA DOCENTES (Colegio -> Profesor -> Mes) ----
                const teacherRoles = ["editingteacher", "profesorgenerartarea", "teacher"];
                let dataParaDocentes = filtrados.filter(d => teacherRoles.includes(d.role_shortname));

                // Filtrar por docentes elegidos en el Select múltiple (si hay)
                if (appliedFilters.docentes && appliedFilters.docentes.length > 0) {
                    dataParaDocentes = dataParaDocentes.filter(d => appliedFilters.docentes.includes(`${d.user_fname} ${d.user_sname}`));
                }

                const reportMap = {};
                dataParaDocentes.forEach(d => {
                    const college = d.collegeName || "Desconocido";
                    const teacherName = `${d.user_fname} ${d.user_sname}`;
                    const date = new Date(d.event_date);
                    const month = date.toLocaleString("es-ES", { month: "long" }).toLowerCase();
                    const year = date.getFullYear();
                    const periodKey = `${month}-${year}`;
                    
                    const key = `${college}_${teacherName}_${periodKey}`;

                    if (!reportMap[key]) {
                        reportMap[key] = {
                            Colegio: college,
                            Profesor: teacherName,
                            Mes: month,
                            Año: year,
                            tiempoTotalSegundos: 0
                        };
                    }
                    reportMap[key].tiempoTotalSegundos += Number(d.active_seconds || 0);
                });

                const ordenMeses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
                
                const arrayReporteDocentes = Object.values(reportMap).map(item => {
                    const value = appliedFilters.unidad === "minutos"
                        ? Math.round(item.tiempoTotalSegundos / 60)
                        : parseFloat((item.tiempoTotalSegundos / 3600).toFixed(2));

                    return {
                        Colegio: item.Colegio,
                        Profesor: item.Profesor,
                        Mes: item.Mes,
                        Año: item.Año,
                        [appliedFilters.unidad === "minutos" ? "Minutos Totales" : "Horas Totales"]: value
                    };
                }).sort((a, b) => {
                    if (a.Colegio !== b.Colegio) return a.Colegio.localeCompare(b.Colegio);
                    if (a.Profesor !== b.Profesor) return a.Profesor.localeCompare(b.Profesor);
                    if (a.Año !== b.Año) return a.Año - b.Año;
                    return ordenMeses.indexOf(a.Mes) - ordenMeses.indexOf(b.Mes);
                });

                setReporteDocentesData(arrayReporteDocentes);
                // -------------------------------------------------------------

                // Actividad
                let agrupados = [];
                const unidad = appliedFilters.unidad;
                if (appliedFilters.modo === "mes") {
                    agrupados = agruparDataPorMes(filtrados, null, unidad);
                } else if (appliedFilters.modo === "anio") {
                    agrupados = agruparDataPorAnio(filtrados, null, unidad);
                } else {
                    agrupados = agruparDataPorDia(filtrados, null, unidad);
                }
                setDataActividad(agrupados);

                // Distribución
                const map = {};
                filtrados.forEach((d) => {
                    if (!map[d.course_fullname]) {
                        map[d.course_fullname] = 0;
                    }
                    map[d.course_fullname] += Number(d.active_seconds);
                });
                const distArr = Object.entries(map).map(([course_fullname, active_seconds]) => ({
                    course_fullname,
                    active_seconds,
                }));
                setDataDistribucion(distArr);

                // Ranking
                setDataRanking(filtrados);

                // Meta
                const nuevaMeta = calcularMeta({
                    agrupacionModo: appliedFilters.modo,
                    unidadTiempo: unidad,
                    hayFiltros: true
                });
                setMeta(nuevaMeta);
            });
        } else {
            setDataActividad([]);
            setDataDistribucion([]);
            setDataRanking([]);
            setReporteDocentesData([]);
        }
    }, [
        generalData,
        appliedFilters,
        runWithLoader
    ]);

    const getRoleLabel = () => {
        const roles = appliedFilters.roles;
        if (!roles || roles.length === 0) return "Usuarios";
        const hasStudent = roles.includes("student");
        const hasTeacher = roles.includes("teacher");

        if (hasStudent && hasTeacher) return "Usuarios";
        if (hasStudent) return "Estudiantes";
        if (hasTeacher) return "Profesores";

        return "Usuarios";
    };

    const roleLabelText = getRoleLabel();
    const rankingTitle = `Top 10 ${roleLabelText} más activos`;
    const itemLabel = roleLabelText.endsWith('s') ? roleLabelText.slice(0, -1) : roleLabelText;
    const unidadLabel = appliedFilters.unidad === "horas" ? "Horas" : "Minutos";
    const timeKey = appliedFilters.unidad === "minutos" ? "Minutos Totales" : "Horas Totales";

    const filtrosExport = {
        unidadTiempo: appliedFilters.unidad,
        yearSeleccionado: appliedFilters.years,
    };
    const rankingEstudiantesFinal = [...rankingTop, ...rankingBottom];
    const unifiedData = getUnifiedData(dataActividad, pieCursosFinal, rankingEstudiantesFinal, filtrosExport);

    const unifiedHeaders = [
        { label: "Sección", key: "seccion" },
        { label: "Descripción", key: "descripcion" },
        { label: "Fecha", key: "fecha" },
        { label: "Total", key: "total" },
        { label: "Curso", key: "curso" },
        { label: "Tiempo", key: "tiempo" },
        { label: "Profesor", key: "profesor" },
    ];

    if (loadingGeneralData || internalLoading) {
        return <Loader />;
    }

    return (
        <div className="page-container">
            <h1 className="page-title">Dashboard General</h1>

            {/* Export Buttons */}
            <div style={{ textAlign: "center", marginBottom: 20, display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
                <ExportCSVButton
                    data={unifiedData}
                    headers={unifiedHeaders}
                    filename="reporte_general.csv"
                    separator=";"
                    label="Exportar Dashboard General (CSV)"
                />

                {/* NUEVO BOTÓN EXCLUSIVO PARA REPORTE DOCENTES */}
                {reporteDocentesData && reporteDocentesData.length > 0 && (
                    <ExportCSVButton
                        data={reporteDocentesData}
                        headers={[
                            { label: "Colegio", key: "Colegio" },
                            { label: "Profesor", key: "Profesor" },
                            { label: "Mes", key: "Mes" },
                            { label: "Año", key: "Año" },
                            { label: timeKey, key: timeKey }
                        ]}
                        filename="reporte_tiempo_docentes.csv"
                        separator=";"
                        label={`Exportar Uso Docentes (${unidadLabel})`}
                    />
                )}

                <button
                    onClick={async () => {
                        const imgBarra = refBarra.current ? await html2canvas(refBarra.current).then((c) => c.toDataURL()) : null;
                        const imgPie = refPie.current ? await html2canvas(refPie.current).then((c) => c.toDataURL()) : null;
                        const imgRanking = refRanking.current ? await html2canvas(refRanking.current).then((c) => c.toDataURL()) : null;

                        generatePDF({
                            data: unifiedData,
                            headers: unifiedHeaders,
                            filename: "reporte_general.pdf",
                            chartImages: [imgBarra, imgPie, imgRanking],
                        });
                    }}
                    className="btn-primary"
                >
                    Exportar PDF
                </button>
            </div>

            {/* Filters Card */}
            <div className="page-card">
                <div className="page-card-header">
                    <FaUniversity />
                    <span>Filtros Generales</span>
                </div>
                <div className="page-card-body">
                    <div className="filter-grid">
                        <div className="filter-item full-width-mobile">
                            <label>Colegios:</label>
                            <Select
                                isMulti
                                options={opcionesColegios}
                                value={uiColegios}
                                onChange={setUiColegios}
                                placeholder="Seleccionar colegios..."
                                className="basic-multi-select"
                                classNamePrefix="select"
                            />
                            <button
                                className="btn-secondary-small"
                                onClick={() => setUiColegios(opcionesColegios)}
                            >
                                Seleccionar Todos
                            </button>
                        </div>

                        <div className="filter-item">
                            <label><FaUserTie /> Rol (Puedes elegir varios):</label>
                            <Select
                                isMulti
                                options={[
                                    { value: "student", label: "Estudiante" },
                                    { value: "teacher", label: "Profesor" }
                                ]}
                                value={uiRol}
                                onChange={setUiRol}
                                placeholder="Seleccionar Rol"
                                className="basic-multi-select"
                                classNamePrefix="select"
                            />
                        </div>

                        {/* NUEVO FILTRO PARA DOCENTES ESPECÍFICOS (Solo visible si se selecciona Profesor) */}
                        {uiRol.some(r => r.value === "teacher") && (
                            <div className="filter-item full-width-mobile">
                                <label><FaUserTie /> Docentes Específicos:</label>
                                <Select
                                    isMulti
                                    options={opcionesDocentes}
                                    value={uiDocentes}
                                    onChange={setUiDocentes}
                                    placeholder="Todos los docentes..."
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    isDisabled={opcionesDocentes.length === 0}
                                />
                            </div>
                        )}

                        <div className="filter-item">
                            <label>
                                <FaClock /> Unidad de tiempo:
                            </label>
                            <select
                                className="input-responsive"
                                value={unidadTiempo}
                                onChange={(e) => setUnidadTiempo(e.target.value)}
                            >
                                <option value="minutos">Minutos</option>
                                <option value="horas">Horas</option>
                            </select>
                        </div>
                    </div>

                    <div className="filter-grid" style={{ marginTop: "20px", alignItems: "end" }}>
                        <div className="filter-item">
                            <label>Agrupar visualización por:</label>
                            <div className="radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="agrupacion"
                                        value="mes"
                                        checked={agrupacionModo === "mes"}
                                        onChange={() => setAgrupacionModo("mes")}
                                    />
                                    Mes
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="agrupacion"
                                        value="anio"
                                        checked={agrupacionModo === "anio"}
                                        onChange={() => setAgrupacionModo("anio")}
                                    />
                                    Año
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="agrupacion"
                                        value="dia"
                                        checked={agrupacionModo === "dia"}
                                        onChange={() => setAgrupacionModo("dia")}
                                    />
                                    Día
                                </label>
                            </div>
                        </div>

                        {agrupacionModo !== "dia" && (
                            <div className="filter-item">
                                <label><FaCalendarAlt /> Filtrar Años Específicos:</label>
                                <Select
                                    isMulti
                                    options={opcionesAnios}
                                    value={uiYears}
                                    onChange={setUiYears}
                                    placeholder="Seleccionar años..."
                                    isDisabled={generalData.length === 0}
                                />
                            </div>
                        )}

                        {agrupacionModo === "dia" && (
                            <>
                                <div className="filter-item">
                                    <label>Fecha Inicio:</label>
                                    <input
                                        type="date"
                                        className="input-responsive"
                                        value={fechaInicio}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                    />
                                </div>
                                <div className="filter-item">
                                    <label>Fecha Fin:</label>
                                    <input
                                        type="date"
                                        className="input-responsive"
                                        value={fechaFin}
                                        onChange={(e) => setFechaFin(e.target.value)}
                                    />
                                </div>
                            </>
                        )}

                        <div className="filter-item search-button-item">
                            <button
                                className="btn-primary full-width"
                                onClick={handleSearch}
                                disabled={loadingGeneralData}
                                title="Descarga nueva información de la base de datos para los colegios seleccionados"
                            >
                                {loadingGeneralData ? "Cargando..." : "Buscar Data"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts */}
            {generalData.length > 0 ? (
                <>
                    <div className="page-card">
                        <div className="page-card-header">
                            <FaChartBar />
                            <span>Actividad en {unidadLabel}</span>
                        </div>
                        <div className="page-card-body" ref={refBarra}>
                            <BarraActivos data={dataActividad} unidadTiempo={appliedFilters.unidad} meta={meta} />
                        </div>
                    </div>

                    <div className="page-card">
                        <div className="page-card-header">
                            <FaChartBar />
                            <span>Distribución del tiempo por curso ({roleLabelText})</span>
                        </div>
                        <div className="page-card-body" ref={refPie}>
                            <PieCursos
                                data={dataDistribucion}
                                onPieDataChange={setPieCursosFinal}
                            />
                        </div>
                    </div>

                    <div className="page-card">
                        <div className="page-card-header">
                            <FaChartBar />
                            <span>Rankings ({roleLabelText})</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "space-around" }}>
                                <div style={{ flex: "1 1 450px", maxWidth: "100%" }}>
                                    <RankingBarrasEstudiantes
                                        data={dataRanking}
                                        title={rankingTitle}
                                        labelText={itemLabel}
                                        showBottom={false}
                                        showTop={true}
                                        onRankingDataChange={setRankingTop}
                                    />
                                </div>
                                <div style={{ flex: "1 1 450px", maxWidth: "100%" }}>
                                    <RankingBarrasColegios
                                        data={dataRanking}
                                        title="Top 10 Colegios más activos"
                                        type="TOP"
                                        barColor="#ff7300"
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "space-around" }}>
                                <div style={{ flex: "1 1 450px", maxWidth: "100%" }}>
                                    <RankingBarrasEstudiantes
                                        data={dataRanking}
                                        title={`Bottom 10 ${roleLabelText} menos activos`}
                                        labelText={itemLabel}
                                        showBottom={true}
                                        showTop={false}
                                        onRankingDataChange={setRankingBottom}
                                    />
                                </div>
                                <div style={{ flex: "1 1 450px", maxWidth: "100%" }}>
                                    <RankingBarrasColegios
                                        data={dataRanking}
                                        title="Top 10 Colegios menos activos"
                                        type="BOTTOM"
                                        barColor="#82ca9d"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div style={{ textAlign: "center", marginTop: "50px" }}>
                    <h3>Seleccione al menos un colegio para ver los datos.</h3>
                </div>
            )}
        </div>
    );
};

export default GeneralPage;